// ./src/components/layouts/DashboardLayout/DesktopSidebar.tsx
import React, { useCallback } from "react";
import {
  Box,
  Group,
  Text,
  NavLink,
  ScrollArea,
  Stack,
  Divider,
  Avatar,
  Menu,
  UnstyledButton,
  ActionIcon,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { SiteMapItem } from "@/config";
import { User } from "@/utils/api/UserService";

import { Logo } from "@/components/generic/Logo";
import { AppIcon } from "@/components/generic/AppIcon";

import { DEFAULT_ICON_COLOR } from "./constants";

// ============================================
// Types
// ============================================

interface DesktopSidebarProps {
  navigationItems: SiteMapItem[];
  user: User | null | undefined;
  userInitials: string;
  isOwner: boolean;
  appVersion: string;
  currentPath: string;
  onNavClick: (path: string) => void;
  onToggleCollapse: () => void;
  onSignOut: () => void;
  onOpenSupportDrawer: () => void;
  getTranslatedName: (translationKey?: string, fallback?: string) => string;
  isItemActive: (item: SiteMapItem) => boolean;
  t: (key: string) => string;
}

// ============================================
// Component
// ============================================

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  navigationItems,
  user,
  userInitials,
  isOwner,
  appVersion,
  currentPath,
  onNavClick,
  onToggleCollapse,
  onSignOut,
  onOpenSupportDrawer,
  getTranslatedName,
  isItemActive,
  t,
}) => {
  const theme = useMantineTheme();
  const [userMenuOpened, { toggle: toggleUserMenu, close: closeUserMenu }] =
    useDisclosure();

  // ============================================
  // Menu Handlers
  // ============================================

  const handleMenuAction = useCallback(
    (path: string) => {
      onNavClick(path);
      closeUserMenu();
    },
    [onNavClick, closeUserMenu]
  );

  const handleSignOut = useCallback(() => {
    closeUserMenu();
    onSignOut();
  }, [closeUserMenu, onSignOut]);

  // ============================================
  // Navigation Renderer
  // ============================================

  const renderNavItem = (item: SiteMapItem, index: number) => {
    const hasChildren = Array.isArray(item.items) && item.items.length > 0;
    const translatedItemName = getTranslatedName(
      item.options?.translationKey,
      item.name
    );
    const itemIconColor = item.iconColor || DEFAULT_ICON_COLOR;
    const isActive = item.url
      ? currentPath === item.url
      : isItemActive(item);

    if (hasChildren) {
      return (
        <NavLink
          key={`parent-${item.name}-${index}`}
          active={isActive}
          label={translatedItemName}
          leftSection={
            <AppIcon
              iconName={item.icon || "Dashboard"}
              size={26}
              color={isActive ? theme.colors.brand[6] : itemIconColor}
            />
          }
          childrenOffset={28}
          defaultOpened={isActive}
          variant="subtle"
          styles={{
            root: {
              borderRadius: theme.radius.md,
              marginBottom: 2,
            },
            label: {
              fontWeight: 500,
              fontSize: 16,
            },
          }}
        >
          {item.items!.map((child, childIndex) =>
            renderNavItem(child, childIndex)
          )}
        </NavLink>
      );
    }

    if (item.url) {
      return (
        <NavLink
          key={`leaf-${item.url}-${index}`}
          active={currentPath === item.url}
          label={translatedItemName}
          leftSection={
            <AppIcon
              iconName={item.icon || "Dashboard"}
              size={26}
              color={
                currentPath === item.url
                  ? theme.colors.brand[6]
                  : itemIconColor
              }
            />
          }
          onClick={() => onNavClick(item.url!)}
          variant="subtle"
          styles={{
            root: {
              borderRadius: theme.radius.md,
              marginBottom: 2,
            },
            label: {
              fontWeight: 500,
              fontSize: 16,
            },
          }}
        />
      );
    }

    return null;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Stack h="100%" gap={0} bg="neutral.0">
      {/* Desktop Logo Section */}
      <Box
        p="md"
        px="lg"
        mih={64}
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Group justify="space-between" align="center" w="100%">
          <Logo size="small" withName version={appVersion} />
          <Tooltip label="Collapse sidebar" position="right" withArrow>
            <ActionIcon
              variant="subtle"
              color="neutral"
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
              opacity={0.6}
              styles={{
                root: {
                  "&:hover": { opacity: 1 },
                },
              }}
            >
              <AppIcon iconName="ChevronsLeft" size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>

      <Divider color="neutral.2" />

      {/* Navigation */}
      <ScrollArea flex={1} p="xs" px="sm" scrollbarSize={6}>
        <Stack gap={2}>
          {navigationItems.map((item, index) => renderNavItem(item, index))}
        </Stack>
      </ScrollArea>

      <Divider color="neutral.2" />

      {/* Help & Support */}
      <Box px="sm" pb="xs">
        <NavLink
          label={t("navigation.helpAndSupport")}
          leftSection={<AppIcon iconName="Help" size={18} />}
          onClick={onOpenSupportDrawer}
          variant="subtle"
          styles={{
            root: {
              borderRadius: theme.radius.md,
              marginBottom: 2,
              marginTop: 3,
            },
            label: {
              fontWeight: 500,
              fontSize: 16,
            },
          }}
        />
      </Box>

      <Divider color="neutral.2" />

      {/* User Section */}
      <Box p="md">
        {user && (
          <Menu
            shadow="lg"
            width={260}
            position="top-start"
            opened={userMenuOpened}
            onChange={toggleUserMenu}
            withinPortal
            transitionProps={{ transition: "pop", duration: 150 }}
            styles={{
              label: {
                fontSize: 13,
                fontWeight: 600,
                paddingLeft: 12,
                paddingTop: 8,
                paddingBottom: 4,
              },
              item: {
                fontSize: 15,
                fontWeight: 500,
                padding: "12px 14px",
                borderRadius: theme.radius.sm,
                minHeight: 46,
              },
              itemSection: {
                marginRight: 12,
              },
              divider: {
                marginTop: 8,
                marginBottom: 8,
              },
            }}
          >
            <Menu.Target>
              <UnstyledButton
                w="100%"
                p="sm"
                px="md"
                style={{
                  borderRadius: theme.radius.md,
                  backgroundColor: userMenuOpened
                    ? theme.colors.neutral[1]
                    : undefined,
                  transition: "background-color 150ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme.colors.neutral[1];
                }}
                onMouseLeave={(e) => {
                  if (!userMenuOpened) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <Avatar size={36} color="brand" radius="md">
                    {userInitials}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="md" fw={600} truncate c="neutral.8" lh={1.3}>
                      {user.username}
                    </Text>
                    <Text size="sm" truncate c="neutral.5" lh={1.3}>
                      {user.email}
                    </Text>
                  </Box>
                  <AppIcon
                    iconName="ChevronUp"
                    size={14}
                    color={theme.colors.neutral[4]}
                    style={{
                      transition: "transform 150ms ease",
                      transform: userMenuOpened
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                    }}
                  />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown
              style={{
                border: `1px solid ${theme.colors.neutral[2]}`,
                boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.15)",
              }}
              styles={{
                dropdown: {
                  padding: 8,
                },
              }}
            >
              <Menu.Label>{t("pages.account.name")}</Menu.Label>
              <Menu.Item
                leftSection={<AppIcon iconName="User" size={20} />}
                onClick={() => handleMenuAction("/account/profile")}
              >
                {t("pages.userProfilePage.name")}
              </Menu.Item>
              {isOwner && (
                <>
                  <Menu.Item
                    leftSection={<AppIcon iconName="Settings" size={20} />}
                    onClick={() => handleMenuAction("/account/settings")}
                  >
                    {t("pages.accountSettings.name")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<AppIcon iconName="CreditCard" size={20} />}
                    onClick={() => handleMenuAction("/account/subscription")}
                  >
                    {t("pages.subscription.name")}
                  </Menu.Item>
                </>
              )}

              <Menu.Divider />
              <Menu.Item
                leftSection={<AppIcon iconName="Logout" size={20} />}
                color="error"
                onClick={handleSignOut}
              >
                {t("navigation.logout")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Box>
    </Stack>
  );
};